import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { AlertTriangleIcon, DollarSignIcon, FileWarningIcon } from 'lucide-react';
export function PainSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="bg-white py-20 lg:py-28" data-id="element-211">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-212">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-213">
          <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4" data-id="element-214">
            ⚠️ El Problema
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] leading-tight" data-id="element-215">
            ¿Te Suena Familiar?
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-gray-50 rounded-2xl p-8 lg:p-12 border-l-4 border-red-500 mb-12" data-id="element-216">
          <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6" data-id="element-217">
            Te levantas a las <strong data-id="element-218">5 AM</strong>. Trabajas 10, 12 horas.
            Manejas bajo el sol o el frío de USA para enviarle dinero a tu
            familia y construir un futuro.
          </p>
          <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6" data-id="element-219">
            Y de repente, llega <strong className="text-red-600" data-id="element-220">Abril</strong>
            ... y ese cheque de 1099 que parecía una bendición se convierte en
            una <strong className="text-red-600" data-id="element-221">pesadilla</strong>.
          </p>
          <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6" data-id="element-222">
            Tu preparador te dice:{' '}
            <span className="italic" data-id="element-223">"Debes $5,000 al IRS"</span>.
          </p>
          <p className="text-lg lg:text-xl text-gray-800 leading-relaxed font-semibold" data-id="element-224">
            Te quedas frío. Ese dinero era para la renta, para el carro, para
            tus hijos.{' '}
            <span className="text-[#1a2744]" data-id="element-225">
              ¿Por qué nadie te enseñó las reglas del juego?
            </span>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-226">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-[#1a2744] rounded-xl p-6 text-center" data-id="element-227">
            <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-228">
              <DollarSignIcon className="w-7 h-7 text-red-400" data-id="element-229" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-230">
              Impuesto al Desconocimiento
            </h3>
            <p className="text-gray-400 text-sm" data-id="element-231">
              Pagas más porque nadie te explicó las reglas en español.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="bg-[#1a2744] rounded-xl p-6 text-center" data-id="element-232">
            <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-233">
              <FileWarningIcon className="w-7 h-7 text-red-400" data-id="element-234" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-235">
              Información Confusa
            </h3>
            <p className="text-gray-400 text-sm" data-id="element-236">
              Videos de YouTube contradictorios y contadores que cobran
              $200/hora.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="bg-[#1a2744] rounded-xl p-6 text-center" data-id="element-237">
            <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-238">
              <AlertTriangleIcon className="w-7 h-7 text-red-400" data-id="element-239" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-240">
              Miedo Constante
            </h3>
            <p className="text-gray-400 text-sm" data-id="element-241">
              Cada sobre del gobierno te genera ansiedad y estrés.
            </p>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-lg text-gray-600 mt-12" data-id="element-242">
          Mientras tanto, los grandes empresarios usan las{' '}
          <strong data-id="element-243">mismas leyes</strong> para pagar $0. <br data-id="element-244" />
          <span className="text-[#1a2744] font-semibold" data-id="element-245">
            Ellos no son más inteligentes que tú, solo tienen el manual
            correcto.
          </span>
        </motion.p>
      </div>
    </section>;
}